export default function calculateGraph(subOrders) {
	let lookup = subOrders.reduce((acc,c) => ({ ...acc, [c.subOrder.id]: c }), {});

	// Create end delivery nodes
	let nodes = createNodes(subOrders, lookup);
	// console.log(nodes);

	// Dedupe parallel nodes
	let deduped = new Map();
	nodes = dedupeParallels(nodes, deduped);

	// Calculate y position of each node
	nodes.sort((a,b) => a.depth - b.depth);
	let y = 0;
	nodes.forEach((node) => {
		//console.log(node, node.parallel);
		if (node.parallel)
			y += node.siblings.length;
		node.y = y++;
		if (node.siblings && node.siblings.length > 0) {
			for (let sibling of node.siblings) {
				sibling.y = node.y - 1;
			}
		}
	});

	let lines = calculateLines(nodes, deduped);

	nodes = nodes.map(x => ({
		party: x.party,
		x: x.x,
		y: x.y,
		depth: x.depth,
		prev: x.prev,
		next: x.next,
		subOrder: x.subOrder,
	}));

	//console.log(nodes, lines);
	return { nodes, lines };

}

function createNodes(subOrders, lookup) {
	let nodesLookup = {};
	let subOrdersToCheck = subOrders.filter(x => x.subOrder.next.length === 0).map(x => x.subOrder.id);
	for (let endId of subOrdersToCheck) {
		let { subOrder, width, depth } = lookup[endId];
		let node = {
			party: subOrder.deliveryParty,
			x: width,
			depth,
			subOrder: subOrder,
			prev: new Set(),
			next: new Set(),
		};
		nodesLookup[subOrder.id] = node;
	}

	// Go through all subOrders and create despatch nodes
	while (subOrdersToCheck.length > 0) {
		let { subOrder } = lookup[subOrdersToCheck.shift()];
		if (subOrder.prev.length === 0 && !nodesLookup[`${subOrder.id}-start`]) {
			let nextNode = nodesLookup[subOrder.id];
			let node = {
				party: subOrder.despatchParty,
				x: nextNode.x,
				depth: nextNode.depth - 1,
				prev: new Set(),
				next: new Set(),
				subOrder: subOrder,
			};
			node.next.add(nextNode);
			nextNode.prev.add(node);
			nodesLookup[`${subOrder.id}-start`] = node;
		} else {
			for (let prevId of subOrder.prev) {
				let nextNode = nodesLookup[subOrder.id];
				if (nodesLookup[prevId]) { // Node already created, just add links
					let node = nodesLookup[prevId];
					node.next.add(nextNode);
					nextNode.prev.add(node);
				} else {
					let prev = lookup[prevId];
					let node = {
						party: subOrder.despatchParty,
						x: prev.width,
						depth: prev.depth,
						prev: new Set(),
						next: new Set(),
						subOrder: prev.subOrder,
					};
					node.next.add(nextNode);
					nextNode.prev.add(node);
					nodesLookup[prevId] = node;
				}
			}
			subOrdersToCheck.push(...subOrder.prev);
		}
	}
	return Object.values(nodesLookup);
}

function dedupeParallels(nodes, deduped) {
	for (let node of nodes) {
		if (deduped.has(node)) continue;
		if (node.prev.size !== 1 || node.next.size !== 1) continue;

		let prev = node.prev.values().next().value;
		let next = node.next.values().next().value;

		let siblings = [...prev.next].filter(x => x.prev.size === 1 && x.next.size === 1 && x.next.values().next().value === next);
		if (siblings.length === 1) continue;

		let minNode = node;
		for (let sibling of siblings) {
			if (sibling.x < minNode.x)
				minNode = sibling;
		}
		for (let sibling of siblings) {
			if (sibling === minNode) continue;
			deduped.set(sibling, minNode);
		}
		deduped.delete(minNode);
		minNode.parallel = true;
		minNode.siblings = siblings.filter(x => x !== minNode);
	}

	let starts = nodes.filter(x => x.prev.size === 0);
	//console.log(starts);
	deduped = dedupeTerminalNodes(starts, deduped);

	let ends = nodes.filter(x => x.next.size === 0);
	//console.log(ends);
	deduped = dedupeTerminalNodes(ends, deduped);

	return nodes.filter(x => !deduped.has(x));
}

function dedupeTerminalNodes(nodes, deduped) {
	let visited = new Set([]);
	for (let startNode of nodes) {
		if (deduped.has(startNode)) continue;
		if (visited.has(startNode)) continue;

		let siblings = [startNode];
		let minNode = startNode;
		for (let otherNode of nodes) {
			if (startNode === otherNode) continue;

			if (!isPartySame(startNode, otherNode)) continue;
			siblings.push(otherNode);
			if (otherNode.x < minNode.x)
				minNode = otherNode;
		}

		for (let candidate of siblings) {
			if (minNode === candidate) continue;
			deduped.set(candidate, minNode);
		}
		deduped.delete(minNode);
		minNode.parallel = true;
		minNode.siblings = siblings.filter(x => x !== minNode);
	}
	return deduped;
}

// function calculateLines(nodes, deduped) {
// 	let lines = [];
// 	for (let node of nodes) {
// 		for (let nextNode of node.next) {
// 			let line = {
// 				from: node,
// 				via: [],
// 				to: nextNode,
// 				subOrder: nextNode.subOrder
// 			};
// 			if (deduped.has(nextNode)) {
// 				let actualEnd = deduped.get(nextNode);
// 				line = {
// 					from: node,
// 					via: [nextNode],
// 					to: actualEnd,
// 					subOrder: nextNode.subOrder
// 				};
// 			}
// 			lines.push(line);
// 		}
// 	}
// 	return lines;
// }

function calculateLines(nodes, deduped) {
	let lines = [];
	for (let node of nodes) {
		for (let nextNode of node.next) {
			if (deduped.has(nextNode))
				nextNode = deduped.get(nextNode);
			let line = {
				from: node,
				via: [],
				to: nextNode,
				subOrder: nextNode.subOrder
			};
			lines.push(line);
			nextNode.siblings = nextNode.siblings || [];
			for (let siblingNode of nextNode.siblings) {
				let line = {
					from: node,
					via: [siblingNode],
					to: nextNode,
					subOrder: siblingNode.subOrder
				};
				lines.push(line);
			}
		}
	}
	return lines;
}

function isPartySame(partyA, partyB) {
	return partyA.name === partyB.name
		&& partyA.street === partyB.street
		&& partyA.postalCode === partyB.postalCode
		&& partyA.countryCode === partyB.countryCode;
}